import React, { useState, useMemo } from 'react';
import { Search, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import VolunteerRegistration from './VolunteerRegistration';

// Volunteer type definition
interface Volunteer {
  name: string;
  id: string;
  from: string;
  campaigns: number;
  image: string;
}

// Initial volunteers data
const volunteersData: Volunteer[] = [
  {
    name: "Srinivasan. K",
    id: "ADF-NGO-000753610",
    from: "COIMBATORE",
    campaigns: 0,
    image: "https://marketstream.in/img/team/srini.png?auto=compress&cs=tinysrgb&w=300"
  },
  {
    name: "Sasi Kumar. AJ ",
    id: "ADF-NGO-000770681",
    from: "COIMBATORE",
    campaigns: 2,
    image: "https://marketstream.in/img/team/sasikumar.png?auto=compress&cs=tinysrgb&w=300"
  },
  {
    name: "Thamarai Kannan. M",
    id: "ADF-NGO-000778116",
    from: "MANAMADURAI",
    campaigns: 5,
    image: "https://marketstream.in/img/team/kannan.png?auto=compress&cs=tinysrgb&w=300"
  }
];

const Volunteers: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBy, setFilterBy] = useState<'name' | 'id' | 'from'>('name');
  const [showRegistration, setShowRegistration] = useState(false);
  const navigate = useNavigate();

  const filteredVolunteers = useMemo(() => {
    return volunteersData.filter(volunteer => {
      const searchTerm = searchQuery.toLowerCase();
      switch (filterBy) {
        case 'name':
          return volunteer.name.toLowerCase().includes(searchTerm);
        case 'id':
          return volunteer.id.toLowerCase().includes(searchTerm);
        case 'from':
          return volunteer.from.toLowerCase().includes(searchTerm);
        default:
          return true;
      }
    });
  }, [searchQuery, filterBy]);

  const handleRegistrationClick = () => {
    setShowRegistration(true);
  };

  if (showRegistration) {
    return <VolunteerRegistration />;
  }

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-primary mr-3" />
              <h1 className="text-3xl md:text-4xl font-bold">List of Volunteers</h1>
            </div>
            <button
              onClick={handleRegistrationClick}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Register as Volunteer
            </button>
          </div>

          <div className="mb-8 bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search volunteers..."
                  className="w-full pl-10 pr-4 py-2 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex-shrink-0">
                <select
                  className="w-full md:w-auto px-4 py-2 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value as 'name' | 'id' | 'from')}
                >
                  <option value="name">Search by Name</option>
                  <option value="id">Search by ID</option>
                  <option value="from">Search by Location</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredVolunteers.map((volunteer) => (
              <div
                key={volunteer.id}
                className="bg-card rounded-lg overflow-hidden shadow-subtle dark:shadow-subtle-dark border border-border hover:shadow-lg transition-shadow duration-300 animated-section"
              >
                <div className="aspect-square relative">
                  <img
                    src={volunteer.image}
                    alt={volunteer.name}
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-1">{volunteer.name}</h3>
                  <p className="text-sm text-muted-foreground mb-2">ID: {volunteer.id}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{volunteer.from}</span>
                    <span className="text-sm font-medium text-primary">
                      {volunteer.campaigns} Campaigns
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Volunteers;